// Lib
import React from 'react';
import PropTypes from 'prop-types';
import * as Rematrix from 'rematrix';
import classNames from 'classnames';

// Utils
import { convertMatrixToCssTransform, getTransformOrigin } from '../../utils/cssUtil';
import * as matrixLib from '../../../common/maths/matrix/matrixUtils';
import * as pointsLib from '../../../common/maths/geometry/point';
import { hasChanged } from '../../utils/react/propsComparisons';
import { prop } from '../../../common/utils/immutableHelper';

// State
import dragAndDropStateSingleton from './dragAndDropStateSingleton';

// Components
import HoverTransformTarget from './HoverTransformTarget';
import DebugUIPoint from '../../debug/ui/DebugUIPoint';

// Constants
import { DROP_TARGET_TYPES } from '../../../common/elements/elementConstants';

// Styles
import './ElementDragGroupPositioner.scss';

const groupPositionHasChanged = hasChanged('groupPosition');
const dragModifierKeysHaveChanged = hasChanged('dragModifierKeys');
const isReplaceModeActiveHasChanged = hasChanged('isReplaceModeActive');

/**
 * Creates a 3D matrix transformation to account for the group position and the scale of the canvas.
 */
const getGroupPositionStyle = (props) => {
    const { groupPosition, zoomScaleOnDragSource } = props;

    return { transform: `translate(${groupPosition.x}px, ${groupPosition.y}px) scale(${zoomScaleOnDragSource})` };
};

/**
 * Create a 3D matrix transformation to update the size and drag offset of the drag preview based on where it
 * is hovering.
 */
const getGroupSizeStyle = (props) => {
    const { zoomScaleOnHoveredTarget, zoomScaleOnDragSource } = props;

    let matrix = Rematrix.identity();
    matrix = matrixLib.setScale(matrix, zoomScaleOnHoveredTarget / zoomScaleOnDragSource);

    const unscaledGrabOffset = pointsLib.reverseScale(
        zoomScaleOnDragSource,
        dragAndDropStateSingleton.scaledGrabOffset,
    );

    return {
        ...getTransformOrigin(unscaledGrabOffset),
        ...convertMatrixToCssTransform(matrix),
    };
};

class ElementDragGroupPositioner extends React.Component {
    constructor(props) {
        super(props);

        // FIXME Don't think this is needed anymore
        // See "Chrome Drop Blink" below for why this is used.
        this.lastRender = null;
    }

    shouldComponentUpdate(nextProps) {
        return (
            groupPositionHasChanged(this.props, nextProps) ||
            dragModifierKeysHaveChanged(this.props, nextProps) ||
            isReplaceModeActiveHasChanged(this.props, nextProps)
        );
    }

    render() {
        const { dragOrigin, children, dragModifierKeys, isReplaceModeActive, groupPosition, dndDebugToolProperties } =
            this.props;

        // Chrome Drop Blink:
        // This is used to avoid the flash in chrome when dropping.
        // Strangely the "sourceClientOffset" (and other properties) become null prior to "isDragging" becoming false.
        // This results in the dragged element rendering for one frame as display: none prior to the drag layer
        // being removed and the dragged element being displayed.
        // By saving the last render, when the ElementDragLayer is still being rendered , but the sourceClientOffset
        // is null, the previous rendered frame will be returned so there is no flash on drop.
        if (!groupPosition) return this.lastRender;

        const debugShowElementDragGroupPositioner = prop('showElementDragGroupPositioner', dndDebugToolProperties);

        const replaceModeEffect = isReplaceModeActive ? DROP_TARGET_TYPES.IMAGE : '';

        this.lastRender = (
            <div
                className={classNames('ElementDragGroupPositioner', ...this.props.hoveredTypes, replaceModeEffect)}
                style={getGroupPositionStyle(this.props)}
            >
                {debugShowElementDragGroupPositioner && (
                    <DebugUIPoint color="var(--debug-ui-point-element-drag-group-positioner-color)" />
                )}

                <div className="ElementDragGroupResizer" style={getGroupSizeStyle(this.props)}>
                    <HoverTransformTarget dragOrigin={dragOrigin} dragModifierKeys={dragModifierKeys}>
                        {children}
                    </HoverTransformTarget>
                </div>
            </div>
        );

        return this.lastRender;
    }
}

ElementDragGroupPositioner.propTypes = {
    groupPosition: PropTypes.object,
    hoveredTypes: PropTypes.array,
    dragOrigin: PropTypes.object,
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    dragModifierKeys: PropTypes.object,
    isReplaceModeActive: PropTypes.bool,
    dndDebugToolProperties: PropTypes.object,
};

export default ElementDragGroupPositioner;
